import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Lock, Unlock, MessageSquare, Eraser } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const REGIONS = ["Bern", "Basel", "Lausanne", "Leitung"];
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const ChatModerationPanel = ({ onLogout }) => {
  const [region, setRegion] = useState("Bern");
  const [state, setState] = useState({ messages: [], locked: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/chat/${region}`, { headers: authHeader() });
      setState({ messages: data.messages, locked: data.locked });
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [region, onLogout]);

  useEffect(() => {
    load(true);
    const iv = setInterval(() => load(false), 3000);
    return () => clearInterval(iv);
  }, [load]);

  const toggleLock = async () => {
    setBusy(true);
    try {
      const next = !state.locked;
      await axios.put(`${API}/admin/chat/${region}/lock?locked=${next}`, {}, { headers: authHeader() });
      setState((s) => ({ ...s, locked: next }));
      toast.success(next ? `Chat ${region} gesperrt.` : `Chat ${region} entsperrt.`);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const removeMsg = async (id) => {
    try {
      await axios.delete(`${API}/admin/chat/messages/${id}`, { headers: authHeader() });
      setState((s) => ({ ...s, messages: s.messages.filter((m) => m.id !== id) }));
      toast.success("Nachricht gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const clearChat = async () => {
    try {
      const { data } = await axios.delete(`${API}/admin/chat/${region}/clear`, { headers: authHeader() });
      setState((s) => ({ ...s, messages: [] }));
      toast.success(`${data.deleted_count} Nachricht(en) gelöscht.`);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Chat leeren fehlgeschlagen.");
    }
  };

  return (
    <div data-testid="chat-mod-panel">
      <div className="mb-8">
        <span className="section-eyebrow">Moderation</span>
        <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Chat-Verwaltung</h1>
        <p className="mt-2 text-sm text-cream/60">Alle Kreis-Chats einsehen, Nachrichten löschen, Chat sperren/entsperren.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex gap-2" data-testid="chat-region-tabs">
          {REGIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              data-testid={`chat-region-${r}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                region === r ? "bg-gold text-anthracite border-gold" : "border-white/15 text-cream/70 hover:text-cream"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="chat-clear"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-white/15 text-cream/80 hover:bg-white/10 transition-colors"
              >
                <Eraser size={16} /> Chat leeren
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-cream">Chat {region} leeren?</AlertDialogTitle>
                <AlertDialogDescription className="text-cream/60">
                  Damit werden <span className="text-cream/90 font-medium">ALLE Nachrichten</span> in diesem Chat dauerhaft gelöscht. Dies kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                <AlertDialogAction data-testid="chat-clear-confirm" onClick={clearChat} className="bg-red-600 text-white hover:bg-red-700">Alles löschen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button
            onClick={toggleLock}
            disabled={busy}
            data-testid="chat-lock-toggle"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
              state.locked ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"
            }`}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : state.locked ? <Unlock size={16} /> : <Lock size={16} />}
            {state.locked ? "Chat entsperren" : "Chat sperren"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gold/15 bg-white/[0.02]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gold/15">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cream/80"><MessageSquare size={16} style={{ color: "#E5C76B" }} /> Chat {region}</span>
          {state.locked && <span className="inline-flex items-center gap-1.5 text-xs text-red-400"><Lock size={13} /> Gesperrt</span>}
        </div>

        <div ref={scrollRef} className="max-h-[520px] overflow-y-auto p-4 space-y-2" data-testid="chat-mod-messages">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-cream/50"><Loader2 size={24} className="animate-spin" /></div>
          ) : state.messages.length === 0 ? (
            <p className="text-sm text-cream/40 text-center py-12" data-testid="chat-mod-empty">Noch keine Nachrichten in diesem Chat.</p>
          ) : (
            state.messages.map((m) => (
              <div key={m.id} data-testid={`chat-mod-msg-${m.id}`} className="flex items-start justify-between gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold">
                    <span style={{ color: "#C69C22" }}>{m.user_rank}</span> <span className="text-cream/80">{m.user_name}</span>
                    <span className="text-cream/35 ml-2 font-normal">{fmtTime(m.created_at)}</span>
                  </p>
                  <p className="text-sm text-cream/90 whitespace-pre-wrap break-words mt-0.5">{m.text}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`chat-mod-delete-${m.id}`} aria-label="Nachricht löschen" className="text-cream/40 hover:text-red-400 transition-colors flex-shrink-0"><Trash2 size={15} /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-cream">Nachricht löschen?</AlertDialogTitle>
                      <AlertDialogDescription className="text-cream/60">Diese Nachricht von {m.user_name} wird dauerhaft entfernt.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                      <AlertDialogAction data-testid={`chat-mod-delete-confirm-${m.id}`} onClick={() => removeMsg(m.id)} className="bg-red-600 text-white hover:bg-red-700">Löschen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatModerationPanel;
